/**
 * Mobile Swiper for generic `section` when `carousel_on_mobile` is enabled.
 * Slides stay wrapped always; desktop uses :not(.swiper-container-initialized) CSS for width/gap.
 *
 * Loaded as classic defer script (after swiper.min.js) so window.Swiper exists.
 */

/** @typedef {{ destroy: (deleteInstance?: boolean, cleanStyles?: boolean) => void, update?: () => void }} SectionMobileSwiperInstance */
/** @typedef {new (container: Element, options: Record<string, unknown>) => SectionMobileSwiperInstance} SwiperConstructor */

(function () {
  const MOBILE_MQ = '(max-width: 749px)';
  const SWIPER_RETRY_LIMIT = 40;
  const DEFAULT_SPACE_BETWEEN = 8;

  class SectionMobileCarousel extends HTMLElement {
    /** @type {SectionMobileSwiperInstance | null} */
    #swiper = null;

    /** @type {number} */
    #retryCount = 0;

    /** @type {number | undefined} */
    #retryId;

    /** @type {MediaQueryList} */
    #mq = window.matchMedia(MOBILE_MQ);

    /** @type {HTMLElement | null} */
    #swiperEl = null;

    /** @type {HTMLElement | null} */
    #wrapper = null;

    connectedCallback() {
      this.#swiperEl = this.querySelector('[data-section-mobile-swiper]');
      this.#wrapper = this.querySelector('[data-section-carousel-panel]');

      if (!(this.#swiperEl instanceof HTMLElement) || !(this.#wrapper instanceof HTMLElement)) return;

      // Keep slides wrapped on desktop + mobile so spacing CSS can target them
      this.#wrapSlides();

      this.#mq.addEventListener('change', this.#sync);
      window.addEventListener('load', this.#sync);
      this.#sync();
    }

    disconnectedCallback() {
      this.#mq.removeEventListener('change', this.#sync);
      window.removeEventListener('load', this.#sync);
      this.#destroySwiper();
      this.#clearRetry();
    }

    get #spaceBetween() {
      const raw = getComputedStyle(this).getPropertyValue('--section-swiper-gap').trim();
      const parsed = Number.parseFloat(raw);

      return Number.isFinite(parsed) ? parsed : DEFAULT_SPACE_BETWEEN;
    }

    #sync = () => {
      if (this.#mq.matches) {
        this.#enable();
        return;
      }

      this.#disable();
    };

    #enable() {
      this.#wrapSlides();
      this.#initSwiper();
    }

    #disable() {
      this.#destroySwiper();
      this.#clearInlineSlideMargins();
      this.removeAttribute('data-ready');
    }

    #wrapSlides() {
      if (!this.#wrapper) return;
      if (this.#wrapper.querySelector(':scope > .swiper-slide')) return;

      const children = [...this.#wrapper.children];
      if (children.length < 2) return;

      for (const child of children) {
        const slide = document.createElement('div');
        slide.className = 'swiper-slide section-mobile-carousel__slide';
        slide.appendChild(child);
        this.#wrapper.appendChild(slide);
      }
    }

    #clearInlineSlideMargins() {
      if (!this.#wrapper) return;

      const slides = this.#wrapper.querySelectorAll(':scope > .swiper-slide');

      for (const slide of slides) {
        if (!(slide instanceof HTMLElement)) continue;
        slide.style.marginLeft = '';
        slide.style.marginRight = '';
        slide.style.width = '';
      }
    }

    #initSwiper() {
      if (!this.#swiperEl || !this.#wrapper) return;

      const slideCount = this.#wrapper.querySelectorAll(':scope > .swiper-slide').length;
      if (slideCount < 2) return;

      const SwiperCtor = /** @type {Window & { Swiper?: SwiperConstructor }} */ (window).Swiper;

      if (typeof SwiperCtor !== 'function') {
        if (this.#retryCount >= SWIPER_RETRY_LIMIT) return;

        this.#retryCount += 1;
        this.#retryId = window.setTimeout(() => this.#initSwiper(), 120);
        return;
      }

      this.#clearRetry();
      this.#retryCount = 0;

      if (this.#swiper) {
        this.#swiper.update?.();
        this.setAttribute('data-ready', '');
        return;
      }

      const spaceBetween = this.#spaceBetween;

      this.#swiper = new SwiperCtor(this.#swiperEl, {
        slidesPerView: 1.15,
        spaceBetween,
        resistanceRatio: 0.65,
        threshold: 5,
        watchOverflow: true,
        touchStartPreventDefault: false,
        observer: true,
        observeParents: true,
      });

      this.setAttribute('data-ready', '');
    }

    #destroySwiper() {
      this.#clearRetry();
      this.#swiper?.destroy(true, true);
      this.#swiper = null;
      this.#retryCount = 0;
    }

    #clearRetry() {
      if (this.#retryId) {
        clearTimeout(this.#retryId);
        this.#retryId = undefined;
      }
    }
  }

  if (!customElements.get('section-mobile-carousel')) {
    customElements.define('section-mobile-carousel', SectionMobileCarousel);
  }
})();
